<template>
  <section>
    <v-col>
      <!-- Back button -->
      <v-row class="ma-0 mb-4">
        <v-btn color="secondary" class="text-capitalize" @click="onBack">
          {{ $translate('BACK') }}
        </v-btn>
      </v-row>

      <h2>{{ $translate('SUBSCRIPTION_STEP1') }}</h2>
      <!-- Search bar for products -->
      <v-text-field
        v-model="searchQuery"
        :label="$translate('SEARCH_PRODUCTS')"
        prepend-inner-icon="mdi-magnify"
        clearable
        outlined
        dense
        class="mt-3"
      />
      <v-alert v-model="showMinSubscriptionValue" type="info" class="mt-3">
        {{ $translate('MIN_SUBSCRIPTION_VALUE') }}:
        <span class="font-weight-bold">
          {{ formatter.format(minSubscriptionValue) }}
        </span>
      </v-alert>

      <v-row class="ma-0">
        <v-col cols="12" md="6" class="px-0 pr-md-3">
          <h4 class="mb-2">
            {{ $translate('ADD_SUBSCRIPTION_PRODUCTS_TITLE') }}
          </h4>
          <SubscriptionProductsAdd
            :products="filteredProductsNotSelected"
            @add="onAddProduct"
          />
        </v-col>
        <v-col cols="12" md="6" class="px-0 pl-md-3">
          <h4 class="mb-2">
            {{ $translate('USER_SELECTIONS') }}
            <span>({{ productsSelected.length }})</span>
          </h4>
          <SubscriptionProductsSelection
            :products="productsSelected"
            @change="onSelectionChange"
          />
        </v-col>
      </v-row>

      <v-row justify="end" class="ma-0">
        <v-col cols="12" md="4" class="pa-0">
          <section v-if="isProfUser">
            <!-- Subtotal price -->
            <v-row justify="space-between" class="ma-0 text-subtitle-2">
              <div>{{ $translate('SUBTOTAL') }}</div>
              <div>{{ formatter.format(subtotalPrice) }}</div>
            </v-row>
            <!-- Discount -->
            <v-row
              v-if="discountPrice < 0"
              justify="space-between"
              class="ma-0 text-subtitle-2"
            >
              <div>{{ $translate('DISCOUNT') }}</div>
              <div class="red--text">{{ formatter.format(discountPrice) }}</div>
            </v-row>
            <!-- VAT -->
            <v-row justify="space-between" class="ma-0 text-subtitle-2">
              <div>{{ $translate('VAT') }}</div>
              <div>{{ formatter.format(totalVATPrice) }}</div>
            </v-row>

            <!-- deposits -->
            <v-row justify="space-between" class="ma-0 text-subtitle-2" v-if="getDepositValue > 0">
              <div>{{ $translate('DEPOSIT_VALUE') }}</div>
              <div>{{ formatter.format(getDepositValue) }}</div>
            </v-row>
          </section>
          <!-- Total price -->
          <v-row justify="space-between" class="ma-0 text-h6">
            <div>{{ $translate('TOTAL') }}</div>
            <div>{{ formatter.format(totalPrice) }}</div>
          </v-row>
        </v-col>
      </v-row>

      <v-row class="ma-0 mt-4" justify="end">
        <v-btn
          color="primary"
          class="text-capitalize primary-font-color"
          :disabled="nextBtnDisabled"
          @click="onNext"
        >
          {{ $translate('NEXT') }}
        </v-btn>
      </v-row>
    </v-col>
  </section>
</template>

<script>
import {
  removeDefaultItem,
  getTotalRetailPriceExclVAT,
  getTotalPriceExclVAT,
  getDiscount,
  isSameOptionSelections,
  hasCapacity,
  getDeposit,
} from '@/objects/Product';
import SubscriptionProductsAdd from '@/components/subscription/SubscriptionProductsAdd';
import SubscriptionProductsSelection from '@/components/subscription/SubscriptionProductsSelection';

export default {
  name: 'SubscriptionProducts',
  components: {
    SubscriptionProductsAdd,
    SubscriptionProductsSelection,
  },
  data() {
    return {
      productsSelected: [],
      productsNotSelected: [],
      searchQuery: '',
      formatter: new Intl.NumberFormat('nl', {
        style: 'currency',
        currency: 'EUR',
      }),
    };
  },
  computed: {
    allProducts() {
      return this.$store.getters['subscription/allProducts'];
    },
    products() {
      // Products already subscribed
      return this.$store.getters['subscription/products'];
    },
    filteredProductsNotSelected() {
      if (!this.searchQuery) {
        return this.productsNotSelected;
      }

      const query = this.searchQuery.toLowerCase();
      return this.productsNotSelected.filter(product => {
        const productName = product.Product.Name ? product.Product.Name.toLowerCase() : '';
        const productDescription = product.Product.Description ? product.Product.Description.toLowerCase() : '';
        const productId = product.ProductId ? product.ProductId.toString().toLowerCase() : '';

        return productName.includes(query) ||
               productDescription.includes(query) ||
               productId.includes(query);
      });
    },
    totalPrice() {
      if (this.productsSelected == null) {
        return 0;
      }

      let price = 0;

      this.productsSelected.forEach((product) => {
        let deposit = 0;
        deposit = getDeposit(product);
        if (deposit > 0) {
          price += product.Price + deposit;
        } else
        price += product.Price;
      });

      return price;
    },
    totalPriceWithoutDeposit() {
      if (this.productsSelected == null) {
        return 0;
      }
      let price = 0;
      this.productsSelected.forEach((product) => {
          price += product.Price;
      });
      return price;
    },
    pgInfo() {
      return this.$store.getters['Account/partnerGroupDetails'];
    },
    minSubscriptionValue() {
      let minValue = 0;
      if (
        this.pgInfo != null &&
        this.pgInfo.SubscriptionMinimumOrderValue != null
      ) {
        minValue = this.pgInfo.SubscriptionMinimumOrderValue;
      }
      return minValue;
    },
    showMinSubscriptionValue() {
      if (this.minSubscriptionValue > 0) {
        return true;
      }
      return false;
    },
    nextBtnDisabled() {
      if (this.productsSelected == null || this.productsSelected.length === 0) {
        return true;
      }

      if (this.totalPrice < this.minSubscriptionValue) {
        return true;
      }

      return false;
    },
    isProfUser() {
      return this.$store.getters.isProfUser;
    },
    subtotalPrice() {
      if (this.productsSelected == null) {
        return 0;
      }

      let price = 0;

      this.productsSelected.forEach((product) => {
        let priceExclVat = getTotalRetailPriceExclVAT(product);
        if (priceExclVat == null || priceExclVat == 0) {
          priceExclVat = getTotalPriceExclVAT(product);
        }
        if (priceExclVat != null) {
          price += priceExclVat;
        }
      });

      return price;
    },
    totalVATPrice() {
      return this.totalPriceWithoutDeposit - this.subtotalPrice - this.discountPrice;
    },
    discountPrice() {
      if (this.productsSelected == null) {
        return 0;
      }

      let price = 0;

      this.productsSelected.forEach((product) => {
        price += getDiscount(product);
      });

      return price;
    },
    getDepositValue() {
      let deposit = 0.0;
      this.productsSelected.forEach((product) => {
        if(product.Product.DepositValue > 0) {
          deposit += product.Product.DepositValue * product.Quantity;
        }
      });
      return deposit;
    }
  },
  watch: {
    allProducts() {
      this.init();
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      this.productsSelected = [];
      this.productsNotSelected = [];

      // eslint-disable-next-line no-restricted-syntax
      for (const product of this.allProducts) {
        // Filter out products that are not bookable
        let bookable = true;

        if (product.Bookable != null) {
          bookable = product.Bookable;
        }

        if (hasCapacity(product) && bookable) {
          // Remove default item in case of ItemDefs configured
          if (Object.keys(product.ItemDefs).length > 0) {
            removeDefaultItem(product);
          }

          const prodObj = {
            ProductId: product.Id,
            ItemId: '',
            ItemDef: '',
            ItemDefObj: {},
            Quantity: 1,
            Capacity: product.Capacity,
            Price: product.Price,
            Product: product,
            Item: {},
            OptionSelectionValid: true,
            OptionSelection: [],
            OptionSelectionSummary: '',
          };

          this.productsNotSelected.push(prodObj);
        }
      }

      this.productsSelected = [...this.products];
    },
    onAddProduct(product) {
      const index = this.productsSelected.findIndex((prodSelected) => {
        if (prodSelected.OptionSelection && product.OptionSelection) {
          return (
            isSameOptionSelections(
              prodSelected.OptionSelection,
              product.OptionSelection
            ) && prodSelected.ItemId === product.ItemId
          );
        }
        return prodSelected.ItemId === product.ItemId;
      });

      if (index >= 0) {
        // Already in selection list, sum quantity and price
        this.productsSelected[index].Quantity += product.Quantity;
        this.productsSelected[index].Price += product.Price;
      } else {
        this.productsSelected.push(product);
      }
    },
    onSelectionChange(newSelections) {
      this.productsSelected = [...newSelections];
    },
    onBack() {
      this.$emit('back');
    },
    onNext() {
      this.$store.commit('subscription/setProducts', this.productsSelected);
      this.$emit('next');
    },
  },
};
</script>
